import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { GrupoDTO } from '../../../models/grupo.model';
import { GrupoService } from '../../../services/grupo/grupo.service';

@Component({
  selector: 'app-grupo-adicionar-dialog',
  templateUrl: './grupo-adicionar-dialog.component.html',
  styleUrls: ['./grupo-adicionar-dialog.component.scss'],
})
export class GrupoAdicionarDialogComponent implements OnChanges, OnInit, OnDestroy {

  // === INPUTS ===
  @Input() visible = false;

  // === OUTPUTS ===
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() grupoCriado = new EventEmitter<GrupoDTO>();

  // === ESTADO INTERNO ===
  nome: string = '';
  imagemBase64: string | null = null;
  imagemNome: string = '';
  salvando: boolean = false;

  // === TRADUCOES ===
  texts: {
    titulo: string;
    subtitulo: string;
    labels_nome: string;
    labels_imagem: string;
    placeholders_nome_da_categoria: string;
    botoes_fechar: string;
    botoes_guardar: string;
    botoes_escolher_imagem: string;
    mensagens_campos_obrigatorios: string;
    mensagens_categoria_cadastrada: string;
    mensagens_erro_cadastrar: string;
  } = {
    titulo: '',
    subtitulo: '',
    labels_nome: '',
    labels_imagem: '',
    placeholders_nome_da_categoria: '',
    botoes_fechar: '',
    botoes_guardar: '',
    botoes_escolher_imagem: '',
    mensagens_campos_obrigatorios: '',
    mensagens_categoria_cadastrada: '',
    mensagens_erro_cadastrar: '',
  };

  private langSub?: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private grupoService: GrupoService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.resetar();
    }
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  // === METODOS PUBLICOS ===

  /** Converte arquivo de imagem para base64 */
  onImageSelect(event: any): void {
    const file = event.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      // Extrair apenas o base64 (sem o prefixo data:image/...)
      const result = reader.result as string;
      this.imagemBase64 = result.split(',')[1] || result;
      this.imagemNome = file.name;
    };
    reader.readAsDataURL(file);
  }

  /** Limpa imagem selecionada */
  onImageClear(): void {
    this.imagemBase64 = null;
    this.imagemNome = '';
  }

  /** Submete o formulario de nova categoria */
  submit(): void {
    if (!this.nome || !this.nome.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.texts.mensagens_campos_obrigatorios,
      });
      return;
    }

    this.salvando = true;

    const body: GrupoDTO = {
      nome: this.nome.trim(),
      status: true,
      imagem: this.imagemBase64 || undefined,
    };

    this.grupoService.post('/grupo', body).subscribe({
      next: (grupoCriado: GrupoDTO) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.texts.mensagens_categoria_cadastrada,
        });
        this.grupoCriado.emit(grupoCriado);
        this.resetar();
        this.fechar();
      },
      error: () => {
        this.salvando = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.texts.mensagens_erro_cadastrar,
        });
      },
    });
  }

  /** Fecha o dialog */
  fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  // === METODOS PRIVADOS ===

  /** Limpa o estado do formulario */
  private resetar(): void {
    this.nome = '';
    this.imagemBase64 = null;
    this.imagemNome = '';
    this.salvando = false;
  }

  /** Carrega traducoes do namespace select-categoria.dialog */
  private loadTranslations(): void {
    const prefix = 'select-categoria.dialog';
    this.texts = {
      titulo: this.translate.instant(`${prefix}.titulo`),
      subtitulo: this.translate.instant(`${prefix}.subtitulo`),
      labels_nome: this.translate.instant(`${prefix}.labels.nome`),
      labels_imagem: this.translate.instant(`${prefix}.labels.imagem`),
      placeholders_nome_da_categoria: this.translate.instant(`${prefix}.placeholders.nome_da_categoria`),
      botoes_fechar: this.translate.instant(`${prefix}.botoes.fechar`),
      botoes_guardar: this.translate.instant(`${prefix}.botoes.guardar`),
      botoes_escolher_imagem: this.translate.instant(`${prefix}.botoes.escolher_imagem`),
      mensagens_campos_obrigatorios: this.translate.instant(`${prefix}.mensagens.campos_obrigatorios`),
      mensagens_categoria_cadastrada: this.translate.instant(`${prefix}.mensagens.categoria_cadastrada`),
      mensagens_erro_cadastrar: this.translate.instant(`${prefix}.mensagens.erro_cadastrar`),
    };
  }
}
